import { PrebidMagniteSlotMapObject } from 'typings/magnite-prebid';
import { messageLog } from 'utilities/helpers/logger/message-logger';
import { browserGlobalThis, getWindow, setWindow } from 'utilities/window';

const TAG_COMMAND_INIT: googletag.CommandArray = [];
// set global failsafe timeout ~500ms after DM UI bidder timeout
const FAILSAFE_TIMEOUT = 3500;
const GA_COOKIE_NAME = '_ga';

const getWindowLcl = (trace: string): browserGlobalThis => {
  try {
    const lclWindow = getWindow();
    if (!lclWindow) {
      throw new Error(`No window object found: ${trace}`);
    }
    return lclWindow;
  } catch (e) {
    messageLog.error(`No window object found`, ['error', e]);
    return {} as browserGlobalThis;
  }
};

/** Returns GA client id from `_ga` cookie format `GA1.1.123456789.1234567890` 
 *  The cookie is almost guranteed to be present as it's set by GA as the page loads 
*/

export const parseGoogleAnalyticsIdFromCookie = (): string => {
  try {

    const cookieString = getWindowLcl('ga-cookie')?.document?.cookie;
    if (!cookieString) {
      return "";
    }
    
    const gaCookie = cookieString
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${GA_COOKIE_NAME}=`));
    
    if (!gaCookie) {
      return "";
    }
    
    const gaCookieValue = decodeURIComponent(gaCookie.substring(`${GA_COOKIE_NAME}=`.length));
    const gaCookieParts = gaCookieValue.split('.');
    
    if (gaCookieParts.length < 4 || !gaCookieParts[0].startsWith('GA')) {
      return "";
    }
    
    return `${gaCookieParts[2]}.${gaCookieParts[3]}`;
  } catch (e) {
    messageLog.error(`Error parsing GA cookie`, ['error', e]);
    return "";
  }
};

/**
 * How the command queue pattern works:
 * Before the script loads: Your code initializes googletag with a cmd array:
 * You push commands to the queue:
 * typescript   lclWindow.googletag.cmd.push(() => {
 *   lclWindow.googletag.setConfig({ disableInitialLoad: true });
 * });
 * When the GPT script loads: It checks if googletag.cmd already exists, preserves the array, and executes all queued commands in order
 * This is the standard pattern used by both Google Publisher Tag (GPT) and Prebid.
 * https://developers.google.com/publisher-tag/reference#googletag.cmd
 * Debug with ?google_console=1 in URL
 */
const getWindowGoogletag = () => {
  const lclWindow = getWindowLcl('tag');
  if (!lclWindow.googletag || !lclWindow.googletag.cmd) {
    const windowGoogletag = { cmd: TAG_COMMAND_INIT } as typeof googletag;
    setWindow('googletag', windowGoogletag);
    return windowGoogletag;
  }
  return lclWindow.googletag;
};

export const getGoogleTag = ((hasInit?: boolean) => {
  return (): NonNullable<typeof googletag> => {
    const gtag = getWindowGoogletag();

    if (!hasInit && googletag) {
      gtag.cmd.push(() => {
        googletag.setConfig({
          disableInitialLoad: true,
          targeting: { client_id: parseGoogleAnalyticsIdFromCookie() }
        });
        googletag.enableServices(); // Lock in configuration
      });
      hasInit = true;
    }

    return googletag as NonNullable<typeof googletag>;
  };
})();

/** Debug with ?pbjs_debug=true in URL */
export const getPreBidJs = ((hasInit?: boolean) => {
  return () => {
    const lclWindow = getWindowLcl('prebid');

    if (!lclWindow?.pbjs) {
      // Initialize queue if pbjs isn't loaded yet
      setWindow('pbjs', { que: [] } as unknown as PrebidJSMagnite);
    }

    const pbjs = lclWindow.pbjs;

    if (!hasInit) {
      // This will execute when pbjs loads
      pbjs.que.push(() => {
        pbjs.setConfig({
          // debug: true,
        });
      });
      hasInit = true;
    }

    return pbjs;
  };
})();

// function that handles the failsafe using boolean logic per auction
function magniteFailsafeHandler(
  callback: (slotsToRefresh?: googletag.Slot[]) => void,
  initialSlots: googletag.Slot[] | PrebidMagniteSlotMapObject[],
) {
  let adserverRequestSent = false;
  return (bidsBackSlots: googletag.Slot[]) => {
    if (adserverRequestSent) return;
    adserverRequestSent = true;
    callback(bidsBackSlots || initialSlots);
  };
}

// example function to call whenever a DM header bidding auction is required
// DM asynchronous integrations are ad server agnostic; they can be used with any ad server and do not require the GPT library.
// Note: When using an asynchronous integration it is recommended to manually set the Global Placement ID (GPID) value to increase demand.
// More information on how to do this can be found in the Advanced Use Cases section of the Demand Manager Web Integration Guide.
export function demandManagerRequestMagniteSlots(slotMap: PrebidMagniteSlotMapObject[], pbjs: PrebidJSMagnite) {
  // provide failsafeHandler with callback function to fire when we want to make
  // the ad server request, as well as slotMap to use in case of failsafe
  const sendAdServerRequest = magniteFailsafeHandler((_?: googletag.Slot[]) => {
    const googletag = getGoogleTag();

    // wait for GPT to load
    googletag.cmd.push(function () {
      // define an array of GPT slots to refresh
      const slotsToRefresh: googletag.Slot[] = [];

      // loop through slot map (not bids) in case of no bids
      slotMap.forEach((slotMapObject) => {
        // map the slot map object to a GPT slot
        const gptSlot = googletag
          .pubads()
          .getSlots()
          .find((gptSlot) => {
            return gptSlot.getSlotElementId() === slotMapObject.divId;
          });

        // manage ad-server targeting key/value pairs if matched and
        // add GPT slot to array of slots to refresh
        if (gptSlot) {
          pbjs.setTargetingForGPTAsync(gptSlot.getSlotElementId());
          slotsToRefresh.push(gptSlot);
        }
      });

      // refresh the array of GPT slots
      if (slotsToRefresh.length) {
        googletag.pubads().refresh(slotsToRefresh);
      }
    });
  }, slotMap);

  // request bids when PBJS is ready
  pbjs.rp.requestBids({
    callback: sendAdServerRequest,
    slotMap: slotMap,
    divPatternMatching: true,
  });

  // start the failsafe timeout
  setTimeout(sendAdServerRequest, FAILSAFE_TIMEOUT);
}
